<script lang="ts">
  import { settingsStore } from "../lib/stores/settings";
  import type { TurnstileEventAttributes, TurnstileEventDetail } from "@svelte-put/cloudflare-turnstile";
  import * as Bugsnag from "../lib/bugsnag";
  import { turnstile } from "@svelte-put/cloudflare-turnstile";
  import { sessionStore } from "../lib/stores/session";
  import { completeTurnstile } from "../lib/helpers/auth/auth";
  import { showCaptcha } from "../lib/helpers/captcha";

  export let route: string;

  const turnstileSiteKey = import.meta.env.FOOJI_TURNSTILE_SITE_KEY as string;

  const onTurnstileComplete: TurnstileEventAttributes["on:turnstile"] = (e) => {
    void (async () => {
      await doOnTurnstileComplete(e.detail);
    })();
  };

  const doOnTurnstileComplete = async (detail: TurnstileEventDetail<{ token: string }>) => {
    // This condition is just to apease typescript, because this should not run if campaign is not set, as the splash will be hidden
    if (campaign) {
      await completeTurnstile(detail.token, campaign);
    }
  };

  // By default, Turnstile will try again
  const onTurnstileExpired = () => {
    console.log("Turnstile expired");
  };

  const onTurnstileError: TurnstileEventAttributes["on:turnstile:error"] = (e) => {
    console.error("Turnstile error", e.detail);
    const turnstileErrorCode = e.detail?.code;
    // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
    // 300*** (300030 or 300031 for example): Possible bot detected
    // 600*** (600010 for example): Possible bot detected
    // 106*** (106010 for example): Invalid Parameters. Continuous failures may indicate a bot
    // 11060* (110600 for example): User probably took too long to complete the captcha
    // 100*** (100040 for example): Multiple errors with this code might indicate an automated attempts
    if (![/^300/, /^11060/, /^106/, /^600/, /^100/].some((regex) => regex.test(turnstileErrorCode))) {
      Bugsnag.getClient().notify(new Error(`Turnstile error ${JSON.stringify(e.detail)}`));
    }
  };

  // For now just log to Bugsnag, hopefully this will be very rare
  const onTurnstileUnsupported: TurnstileEventAttributes["on:turnstile:unsupported"] = (e) => {
    console.error("Browser unsupported by turnstile", e.detail);
    Bugsnag.getClient().notify(new Error(`Browser unsupported by turnstile ${JSON.stringify(e.detail)}`));
  };

  $: campaign = $settingsStore.campaign;
  $: turnstileAction = campaign ? campaign.slice(0, 32) : "unknown";
  $: shouldShowCaptcha = showCaptcha({
    route,
    turnstileTokenData: $sessionStore.turnstileTokenData,
    settings: $settingsStore
  });
</script>

{#if shouldShowCaptcha}
  <div
    class="fixed top-20 left-0 z-10 flex justify-center items-center bg-white opacity-90 h-screen w-screen blur-xs" />
  <div class="fixed top-20 left-0 flex z-20 items-center justify-center h-full w-full">
    <div
      class="mb-4 mt-[-10rem]"
      data-testid="captcha"
      use:turnstile
      turnstile-sitekey={turnstileSiteKey}
      turnstile-theme="light"
      turnstile-action={turnstileAction}
      on:turnstile={onTurnstileComplete}
      on:turnstile:expired={onTurnstileExpired}
      on:turnstile:error={onTurnstileError}
      on:turnstile:unsupported={onTurnstileUnsupported} />
  </div>
{/if}
